import React, { useState, useRef, useEffect } from "react";
import {
  CheckCircle2, ChevronDown, ChevronUp,
  RotateCcw, Download, X, Loader2, AlertCircle, FileText, ExternalLink,
} from "lucide-react";
import ReportPrintView from "./ReportPrintView";
import { downloadReportPdf } from "../../../utility/downloadReportPdf";
import { formatPolicyDate } from "../../../utility/formatPolicyDate";
import { getAssetSnapshot, openAssetSnapshotPdf } from "../services/dashboardService";

// ── Shared score rows ─────────────────────────────────────────────────────────
export const SCORE_ROWS = [
  { label: "Data Usage",                  score: 6, tone: "text-warning"     },
  { label: "Data Sharing",                score: 7, tone: "text-primary"     },
  { label: "Security Commitments",        score: 4, tone: "text-orange-500"  },
  { label: "Confidentiality Protections", score: 8, tone: "text-success"     },
  { label: "Data Retention & Deletion",   score: 3, tone: "text-danger"      },
];

// ── Shared mock result ────────────────────────────────────────────────────────
export const MOCK_RESULT = {
  title: "Privacy and Data Handling Review",
  reviewedAt: new Date().toISOString().replace("T", " ").slice(0, 19),
  overallScore: 3.4,
  categoriesAnalyzed: 5,
  avgConfidence: 91,
  toolSummary:
    "This is a leading health solutions company that aims to simplify healthcare by offering a wide range of services including health services, health insurance, pharmacy services, and prescription drug coverage.",
  executiveSummary:
    "The privacy practices raise significant concerns regarding data sharing, data usage, and confidentiality. The policy explicitly states that certain personal information may be sold or shared for targeted advertising.",
  categories: [
    { name: "Data Sharing",    score: 3, maxScore: 10, confidence: 95, verdict: "Worse", description: "CVS shares personal information with service providers, third-party ad networks, and research partners." },
    { name: "Data Usage",      score: 3, maxScore: 10, confidence: 80, verdict: "Worse", description: "The policy explicitly states that inputs to AI tools may be recorded and used to train bots." },
    { name: "User Rights",     score: 5, maxScore: 10, confidence: 88, verdict: "Fair",  description: "Users can opt out of targeted advertising and request deletion, but the process is cumbersome." },
    { name: "Security",        score: 4, maxScore: 10, confidence: 92, verdict: "Fair",  description: "The policy mentions safeguards but lacks specific certifications or technical controls." },
    { name: "Transparency",    score: 2, maxScore: 10, confidence: 97, verdict: "Worse", description: "The policy uses vague language around data monetization and retention periods." },
  ],
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const verdictTone = (indicator = "") => {
  const v = indicator.toLowerCase();
  if (["good", "strong", "excellent"].includes(v)) return "text-success";
  if (["neutral", "fair", "moderate"].includes(v)) return "text-warning";
  return "text-danger";
};

const badgeClass = (verdict) => {
  const v = (verdict || "").toLowerCase();
  if (v === "good" || v === "strong") return "bg-success/10 text-success";
  if (v === "fair" || v === "moderate") return "bg-warning/10 text-warning";
  return "bg-danger/10 text-danger";
};

const CategoryCard = ({ cat }) => {
  const [open, setOpen] = useState(false);
  const pct = Math.round((cat.score / cat.maxScore) * 100);

  return (
    <div className="border border-surface-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-brand-text">{cat.name}</h4>
        <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${badgeClass(cat.verdict)}`}>
          {cat.verdict}
        </span>
      </div>
      <div className="flex items-center gap-3 mb-1">
        <div className="flex-1 h-2 bg-surface-border rounded-full overflow-hidden">
          <div className="h-full rounded-full bg-success transition-all" style={{ width: `${pct}%` }} />
        </div>
        <span className="text-xs text-brand-muted w-10 text-right">{cat.score}/{cat.maxScore}</span>
      </div>
      <div className="flex items-center gap-3 mb-3">
        <div className="flex-1 h-2 bg-surface-border rounded-full overflow-hidden">
          <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${cat.confidence}%` }} />
        </div>
        <span className="text-xs text-brand-muted w-10 text-right">{cat.confidence}%</span>
      </div>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-xs text-brand-muted hover:text-primary transition-colors"
      >
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {open ? "Hide detail" : "Show detail"}
      </button>
      {open && <p className="mt-2 text-sm text-brand-muted leading-relaxed">{cat.description}</p>}
    </div>
  );
};

// ── Archived source policy ────────────────────────────────────────────────────
// The vendor's own privacy page as we fetched and rendered it when the analysis
// ran — shown next to the agent's findings so the report can be checked against
// its source. Renders nothing at all when we have no asset to look it up by.
export const SourcePolicyCard = ({ assetId, softwareName }) => {
  const [state, setState] = useState({ loading: true, snapshot: null, fetchStatus: null });
  const [opening, setOpening] = useState(false);
  const [openError, setOpenError] = useState(null);

  useEffect(() => {
    if (!assetId) return undefined;
    let cancelled = false;

    getAssetSnapshot(assetId)
      .then((res) => {
        if (cancelled) return;
        const d = res?.data || {};
        setState({ loading: false, snapshot: d.snapshot || null, fetchStatus: d.last_fetch_status || null });
      })
      .catch(() => {
        // The archive is supporting evidence — never surface a failure here as
        // though the analysis itself went wrong.
        if (!cancelled) setState({ loading: false, snapshot: null, fetchStatus: null });
      });

    return () => { cancelled = true; };
  }, [assetId]);

  if (!assetId || state.loading) return null;

  const { snapshot, fetchStatus } = state;

  // Couldn't archive it — say why in plain terms, and make clear the report stands.
  if (!snapshot) {
    const reason = fetchStatus === "blocked"
      ? "The vendor's site refused our request, so we couldn't keep a copy of the page."
      : fetchStatus === "timeout"
        ? "The vendor's site didn't respond in time, so we couldn't keep a copy of the page."
        : "We couldn't save a copy of this policy page.";
    return (
      <div className="bg-white border border-surface-border rounded-2xl p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <FileText className="w-5 h-5 text-brand-light shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-brand-text">Source policy</p>
            <p className="text-sm text-brand-muted mt-0.5">{reason} The analysis above is unaffected.</p>
          </div>
        </div>
      </div>
    );
  }

  const captured = snapshot.fetched_at
    ? new Date(snapshot.fetched_at).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    })
    : null;
  const sizeKb = snapshot.pdf_size_bytes ? Math.round(snapshot.pdf_size_bytes / 1024) : null;
  const hasPdf = Boolean(snapshot.pdf_size_bytes);

  const handleOpen = async () => {
    if (opening) return;
    setOpening(true);
    setOpenError(null);
    try {
      const slug = (softwareName || "policy").toLowerCase().replace(/\s+/g, "-");
      await openAssetSnapshotPdf(assetId, `${slug}-source-policy-v${snapshot.snapshot_version}.pdf`);
    } catch (err) {
      setOpenError(err?.message || "Couldn't open the archived policy. Please try again.");
    } finally {
      setOpening(false);
    }
  };

  return (
    <div className="bg-white border border-surface-border rounded-2xl p-4 sm:p-5">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <FileText className="w-5 h-5 text-primary shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-brand-text">Source policy</p>
            <p className="text-sm text-brand-muted mt-0.5">
              The page this analysis was based on, saved as we read it
              {captured ? ` on ${captured}` : ""}.
            </p>
            <p className="text-xs text-brand-light mt-1">
              Version {snapshot.snapshot_version}
              {snapshot.pdf_page_count ? ` · ${snapshot.pdf_page_count} pages` : ""}
              {sizeKb ? ` · ${sizeKb} KB` : ""}
            </p>
          </div>
        </div>

        {hasPdf && (
          <button
            onClick={handleOpen}
            disabled={opening}
            className="inline-flex items-center gap-2 text-sm text-brand-text border border-surface-border rounded-xl px-4 py-2.5 hover:bg-surface transition-colors disabled:opacity-60 disabled:cursor-not-allowed shrink-0"
          >
            {opening
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Opening…</>
              : <><ExternalLink className="w-4 h-4" /> View source PDF</>}
          </button>
        )}
      </div>
      {openError && (
        <p className="text-sm text-red-600 mt-3">{openError}</p>
      )}
    </div>
  );
};

// ── Main modal ────────────────────────────────────────────────────────────────
const PolicyResultModal = ({ result, loading, error, softwareName, assetId, onClose, onNewAnalysis, versions, activeVersion, onVersionChange }) => {
  const [tab, setTab]             = useState("summary");
  const [pdfLoading, setPdfLoading] = useState(false);
  const printRef = useRef(null);
  const showSidebar = Array.isArray(versions) && versions.length > 0;
  const sourceUrl = result?.sourceUrl || "";
  const displaySoftwareName = softwareName || result?.softwareName || "";
  const snapshotAssetId = assetId || result?.assetId || null;

  const handleExportPdf = async () => {
    if (!printRef.current || pdfLoading) return;
    setPdfLoading(true);
    try {
      const slug = (displaySoftwareName || "report").toLowerCase().replace(/\s+/g, "-");
      await downloadReportPdf(printRef.current, `${slug}-privacy-report`);
    } finally {
      setPdfLoading(false);
    }
  };

  const innerContent = (
    <div className="space-y-4">
          {/* Loading state */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
              <p className="text-sm text-brand-muted">Loading report…</p>
            </div>
          )}

          {/* Error state */}
          {!loading && error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              <AlertCircle className="w-4 h-4 shrink-0" /> {error}
            </div>
          )}

          {/* Content */}
          {!loading && !error && result && (<>
          {/* Analysis header */}
          <div className="bg-white border border-surface-border rounded-2xl p-4 sm:p-5 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 text-success text-sm font-semibold mb-1">
                <CheckCircle2 className="w-4 h-4" /> Analysis Complete
              </div>
              {displaySoftwareName && (
                <p className="text-xl font-extrabold text-brand-text mt-0.5">{displaySoftwareName}</p>
              )}
              {sourceUrl && (
                <p className="text-xs text-primary mt-1 break-all">{sourceUrl}</p>
              )}
              <p className="text-sm text-brand-light mt-0.5">Analyzed on {result.reviewedAt}</p>
              {formatPolicyDate(result.policyAsOf) && (
                <p className="text-sm text-brand-light mt-0.5">Policy As Of {formatPolicyDate(result.policyAsOf)}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {onNewAnalysis && (
                <button
                  onClick={onNewAnalysis}
                  className="inline-flex items-center gap-2 text-sm text-brand-muted border border-surface-border rounded-xl px-4 py-2.5 hover:bg-surface transition-colors"
                >
                  <RotateCcw className="w-4 h-4" /> New Analysis
                </button>
              )}
              <button
                onClick={handleExportPdf}
                disabled={pdfLoading}
                className="inline-flex items-center gap-2 text-sm text-white bg-primary rounded-xl px-4 py-2.5 hover:bg-primary-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {pdfLoading
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</>
                  : <><Download className="w-4 h-4" /> Export PDF</>}
              </button>
            </div>
          </div>

          {/* Archived source policy — the page the agent's report was based on */}
          <SourcePolicyCard assetId={snapshotAssetId} softwareName={displaySoftwareName} />

          {/* Tabs */}
          <div className="bg-white border border-surface-border rounded-2xl overflow-hidden">
            <div className="flex gap-6 border-b border-surface-border px-5 sm:px-6">
              {["summary", "report"].map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`pb-3 pt-4 text-sm font-semibold transition-colors border-b-2 -mb-px capitalize ${
                    tab === t ? "border-primary text-primary" : "border-transparent text-brand-muted hover:text-brand-text"
                  }`}
                >
                  {t === "summary" ? "Summary" : "View Report"}
                </button>
              ))}
            </div>

            {tab === "summary" ? (
              <div className="p-5 sm:p-6 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 items-center">
                <div>
                  <h3 className="text-2xl font-bold text-brand-text mb-4">Policy Overview</h3>
                  <div className="divide-y divide-surface-border border border-surface-border rounded-xl overflow-hidden">
                    {(result.categories || []).map((cat) => (
                      <div key={cat.name} className="flex items-center justify-between px-4 py-3">
                        <span className="text-brand-muted">{cat.name}</span>
                        <span className={`font-bold ${verdictTone(cat.verdict)}`}>{cat.score}/{cat.maxScore}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col items-center text-center">
                  <div className="w-36 h-36 rounded-full border-[10px] border-surface-border border-t-primary flex items-center justify-center">
                    <div>
                      <p className="text-4xl font-extrabold text-primary">{result.overallScore}</p>
                      <p className="text-sm text-brand-light">Overall Score</p>
                    </div>
                  </div>
                  <p className="text-sm text-brand-light max-w-[220px] mt-3">
                    Composite rating across data sharing, usage, retention, security & confidentiality.
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-5 sm:p-6">
                <div className="rounded-xl border border-surface-border bg-surface/40 p-5 max-h-[420px] overflow-y-auto">
                  <div className="bg-white border border-surface-border rounded-xl p-5 space-y-4">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div>
                        {displaySoftwareName && (
                          <h3 className="text-3xl font-extrabold text-brand-text">{displaySoftwareName}</h3>
                        )}
                        {sourceUrl && (
                          <p className="text-xs text-primary break-all">{sourceUrl}</p>
                        )}
                        <p className="text-sm text-brand-muted">Reviewed on {result.reviewedAt}</p>
                        {formatPolicyDate(result.policyAsOf) && (
                          <p className="text-sm text-brand-muted">Policy As Of {formatPolicyDate(result.policyAsOf)}</p>
                        )}
                      </div>
                      <span className="text-xs font-semibold bg-primary/10 text-primary px-3 py-1 rounded-full">
                        PRIVACY ANALYSIS
                      </span>
                    </div>
                    <div className="border border-indigo-100 bg-indigo-50/40 rounded-xl p-4 flex items-center gap-4 flex-wrap">
                      <div className="w-20 h-20 rounded-full border-4 border-primary/20 border-t-primary flex items-center justify-center text-3xl font-bold text-brand-text">
                        {result.overallScore}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-brand-muted font-semibold uppercase">Overall privacy score</p>
                        <p className="text-sm text-brand-muted mt-1">
                          Composite rating across data sharing, usage, retention, security, and confidentiality.
                        </p>
                      </div>
                      <div className="text-right min-w-[140px]">
                        <p className="text-xs text-brand-light uppercase">Categories Analysed</p>
                        <p className="text-3xl font-bold text-primary">{result.categoriesAnalyzed}</p>
                        {result.avgConfidence != null && (<>
                          <p className="text-xs text-brand-light uppercase mt-2">Avg Confidence</p>
                          <p className="text-3xl font-bold text-primary">{result.avgConfidence}%</p>
                        </>)}
                      </div>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-brand-text mb-1">Summaries</h4>
                      <p className="text-xs text-brand-muted mb-3">High-level context for the privacy and data handling posture.</p>
                      <h5 className="text-sm font-semibold text-brand-text mb-1">Tool summary</h5>
                      <p className="text-sm text-brand-muted leading-relaxed">{result.toolSummary}</p>
                    </div>
                    <div>
                      <h5 className="text-sm font-semibold text-brand-text mb-1">Executive summary</h5>
                      <p className="text-sm text-brand-muted leading-relaxed">{result.executiveSummary}</p>
                    </div>
                    <div className="space-y-3 pt-1">
                      {result.categories.map((cat) => (
                        <CategoryCard key={cat.name} cat={cat} />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          </>)}
    </div>
  );

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className={`bg-white rounded-2xl w-full ${showSidebar ? "max-w-5xl" : "max-w-2xl"} max-h-[90vh] overflow-y-auto shadow-2xl`}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-border sticky top-0 bg-white rounded-t-2xl z-10">
          <h2 className="text-xl font-bold text-primary">Analyze Privacy Policy</h2>
          <button onClick={onClose} aria-label="Close" className="text-brand-muted hover:text-danger transition-colors">
            <X className="w-5 h-5 text-danger" />
          </button>
        </div>

        <div className="p-5 sm:p-6">
          {showSidebar ? (
            <div className="grid grid-cols-1 md:grid-cols-[110px_1fr] gap-3">
              {/* Version sidebar */}
              <div className="space-y-2">
                {versions.map((v) => (
                  <button
                    key={v.key}
                    onClick={() => onVersionChange && onVersionChange(v.key)}
                    className={`w-full rounded-lg p-2 text-left border transition-colors ${
                      activeVersion === v.key ? "bg-primary/5 border-primary/20" : "border-surface-border hover:bg-surface"
                    }`}
                  >
                    <p className="text-xs font-semibold text-primary">{v.label}</p>
                    {v.date && <p className="text-[10px] text-brand-light mt-0.5">Analyzed on {v.date}</p>}
                  </button>
                ))}
              </div>
              {/* Main content */}
              <div className="min-w-0">{innerContent}</div>
            </div>
          ) : (
            innerContent
          )}
        </div>
      </div>
    </div>

    {/* Off-screen snapshot rendered for pdf capture */}
    {result && (
      <ReportPrintView ref={printRef} result={result} softwareName={softwareName} />
    )}
    </>
  );
};

export default PolicyResultModal;
